import { useLazyQuery } from "@apollo/client";
import { ClientValue } from "@design-system";
import { FILTERED_CLIENTS_QUERY } from "../../graphql/query";
import { FilterArg, createClient_createClient } from "../../graphql/types";
import { useState, useCallback, useRef, useEffect } from "react";

type PaginationOptions = {
  offset: number;
  limit: number;
};

type QueryOptions = PaginationOptions & {
  filter: FilterArg;
};

export type DuplicateCheckResult = {
  hasDuplicates: boolean;
  duplicates: ClientValue[];
  loading: boolean;
};

export type ClientFilterData = {
  clients: ClientValue[];
  total: number;
  queryVars: QueryOptions;
  search: (search: string) => void;
  fetchMore: () => void;
  loading: boolean;
  checkForDuplicates: (
    clientName: string,
    currentClientId?: string | number
  ) => Promise<{
    hasDuplicates: boolean;
    duplicates: ClientValue[];
  }>;
  duplicateCheckResult: DuplicateCheckResult;
  resetDuplicateCheck: () => void;
};

export const normalizeClientName = (text: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, "") // Remove punctuation
    .trim();
};

export const filterPotentialDuplicates = (
  clients: ClientValue[],
  nameToCheck: string,
  currentClientId?: number
): ClientValue[] => {
  // Extract just the name part if there's an external reference
  const namePartToCheck = nameToCheck.split(" - ")[0].trim();
  const normalizedNameToCheck = normalizeClientName(namePartToCheck);

  return clients.filter((client) => {
    // Skip the current client itself
    if (
      client.id !== undefined &&
      currentClientId !== undefined &&
      Number(client.id) === currentClientId
    ) {
      return false;
    }

    // skip clients that aren't in the PMS
    if (!client.externalId && !client.externalReference) {
      return false;
    }

    const normalizedClientName = normalizeClientName(client.name);
    return (
      normalizedClientName.includes(normalizedNameToCheck) ||
      normalizedNameToCheck.includes(normalizedClientName)
    );
  });
};

export const useClientsQuery = () => {
  const [getClients, queryResult] = useLazyQuery(FILTERED_CLIENTS_QUERY);

  const clients = (queryResult.data?.filteredClients?.clients as unknown as ClientValue[]) || [];
  const total = queryResult.data?.filteredClients?.total || 0;

  return {
    getClients,
    queryResult,
    clients,
    total,
  };
};

/**
 * Hook for checking duplicate clients
 * Can be used independently from the useFilteredClientsLazy hook
 */
export const useClientDuplicateCheck = (
  baseQueryHook = useClientsQuery,
  debounceTimeout: number = 800
) => {
  const [duplicates, setDuplicates] = useState<ClientValue[]>([]);
  const [loading, setLoading] = useState(false);

  const { getClients, clients, queryResult } = baseQueryHook();
  const searchLoading = queryResult.loading;

  // Track if check is in progress
  const checkInProgressRef = useRef(false);
  const lastCheckedValueRef = useRef("");
  const currentClientIdRef = useRef<number | undefined>(undefined);

  // Process search results when they arrive
  useEffect(() => {
    if (!checkInProgressRef.current || !clients || clients.length === 0) {
      return;
    }

    const nameToCheck = lastCheckedValueRef.current;
    const matches = filterPotentialDuplicates(clients, nameToCheck, currentClientIdRef.current);

    setDuplicates(matches);
    setLoading(false);
    checkInProgressRef.current = false;
  }, [clients]);

  const checkForDuplicates = useCallback(
    async (
      clientName: string,
      currentClientId?: string | number
    ): Promise<{
      hasDuplicates: boolean;
      duplicates: ClientValue[];
    }> => {
      if (!clientName || clientName.trim() === "") {
        return { hasDuplicates: false, duplicates: [] };
      }

      try {
        // Extract just the name part if there's an external reference
        const nameOnly = clientName.split(" - ")[0].trim();

        // Direct query without state updates
        const result = await getClients({
          variables: {
            filter: { search: nameOnly },
            offset: 0,
            limit: 20,
          },
        });

        const clientsList =
          (result.data?.filteredClients?.clients as unknown as ClientValue[]) || [];

        // Convert current client ID to number if provided
        const currentClientIdNum =
          currentClientId !== undefined ? Number(currentClientId) : undefined;

        // Find potential duplicates using the shared function
        const matches = filterPotentialDuplicates(clientsList, clientName, currentClientIdNum);

        // Also update the state to keep it in sync
        setDuplicates(matches);
        setLoading(false);

        return {
          hasDuplicates: matches.length > 0,
          duplicates: matches,
        };
      } catch (error) {
        return { hasDuplicates: false, duplicates: [] };
      }
    },
    [getClients]
  );

  const resetDuplicateCheck = useCallback(() => {
    lastCheckedValueRef.current = "";
    currentClientIdRef.current = undefined;
    setDuplicates([]);
    setLoading(false);
  }, []);

  return {
    checkForDuplicates,
    duplicateCheckResult: {
      hasDuplicates: duplicates.length > 0,
      duplicates,
      loading: loading || (searchLoading && checkInProgressRef.current),
    },
    resetDuplicateCheck,
  };
};

// Stub implementation of duplicate check interface for when the feature is disabled
const createEmptyDuplicateCheck = () => ({
  checkForDuplicates: async () => ({ hasDuplicates: false, duplicates: [] }),
  duplicateCheckResult: { hasDuplicates: false, duplicates: [], loading: false },
  resetDuplicateCheck: () => {},
});

/**
 * Main hook for filtered clients with integrated duplicate checking
 */
export const useFilteredClientsLazy = (
  limit: number = 10,
  enableDuplicateCheck: boolean = false
) => {
  // Use the same base query hook
  const { getClients, queryResult, clients, total } = useClientsQuery();
  const {
    loading,
    error,
    fetchMore: fetchMoreRaw,
    variables = { offset: 0, limit: 0, filter: { name: [""] } },
  } = queryResult;

  // Always initialize the duplicate check hook, but with a flag to enable/disable
  const duplicateCheck = useClientDuplicateCheck(
    // Pass the same base query hook to ensure they share the same query
    useClientsQuery,
    800
  );

  // Create a reference to either the real hook or the empty implementation
  const {
    checkForDuplicates: checkForDuplicatesAsync,
    duplicateCheckResult,
    resetDuplicateCheck,
  } = enableDuplicateCheck ? duplicateCheck : createEmptyDuplicateCheck();

  // Create the main search function
  const search = useCallback(
    (searchTerm: string) => {
      getClients({
        variables: {
          filter: { search: searchTerm },
          offset: 0,
          limit,
        },
      });

      // Reset duplicate check when doing a new search
      if (enableDuplicateCheck) {
        resetDuplicateCheck();
      }
    },
    [getClients, limit, enableDuplicateCheck, resetDuplicateCheck]
  );

  const checkForDuplicates = useCallback(
    async (clientName: string, currentClientId?: string | number) => {
      if (!enableDuplicateCheck) return { hasDuplicates: false, duplicates: [] };
      return checkForDuplicatesAsync(clientName, currentClientId);
    },
    [enableDuplicateCheck, checkForDuplicatesAsync]
  );

  return {
    clients,
    total,
    loading,
    error,
    fetchMore: () => {
      fetchMoreRaw &&
        fetchMoreRaw({
          variables: {
            offset: clients?.length || 0,
          },
        });
    },
    search,
    queryVars: variables as QueryOptions,
    checkForDuplicates,
    duplicateCheckResult,
    resetDuplicateCheck,
  };
};

export const clientPresentInSearch = (
  search?: string | null,
  client?: createClient_createClient
): boolean => {
  if (!client || !search) {
    return false;
  }

  const { name, externalReference } = client;
  const searchTerm = search.toLowerCase();

  return (
    name.toLowerCase().includes(searchTerm) ||
    (externalReference || "")?.toLowerCase().includes(searchTerm)
  );
};
